/**
 * Modale dei tag di strategia di una posizione (SPEC-028).
 *
 * Si lavora su una bozza locale e si salva l'insieme finale, come per i tag
 * dei titoli. Due scelte in piu':
 *
 * - **Le incompatibilita' si vedono prima di salvare**: un tag in conflitto con
 *   uno gia' scelto e' spento, con il motivo al passaggio del mouse. Il server
 *   le ricontrolla comunque: e' lui la regola, questa e' solo la cortesia.
 * - **L'orizzonte e' uno solo**: scegliere «Lungo termine» con «Breve termine»
 *   selezionato sostituisce, non somma.
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import { Alert, Button, Field, Modal, Select, Spinner } from '@/ui';
import {
  usePositionStrategy,
  useSavePositionStrategy,
  useStrategyCatalog,
  type StrategyCatalogItem,
  type StrategyParamKey,
  type StrategyParams,
  type StrategyTag,
} from './api';
import { describeParams } from './strategy-format';
import './strategy-tags.css';

type StrategyTagsModalProps = {
  portfolioId: string;
  instrumentId: string;
  instrumentName: string;
  onClose: () => void;
};

const DIMENSION_TITLES: Record<StrategyCatalogItem['dimension'], string> = {
  horizon: 'Orizzonte (uno solo)',
  thesis: 'Perché è stato comprato',
  modifier: 'Rischio',
};

const PARAM_LABELS: Record<StrategyParamKey, string> = {
  eventDate: "Data dell'evento",
  eventTiming: 'Orario',
  eventLabel: 'Evento',
  targetPrice: 'Prezzo obiettivo',
  stopPrice: 'Stop',
  reviewBy: 'Rivedi entro',
  note: 'Nota',
};

const TIMING_OPTIONS = [
  { value: 'before_open', label: "Prima dell'apertura" },
  { value: 'during_session', label: 'Durante la seduta' },
  { value: 'after_close', label: 'Dopo la chiusura' },
  { value: 'unknown', label: 'Non noto' },
];

const CLOSED_REASON: Record<string, string> = {
  position_closed: 'posizione chiusa',
  conflict: 'annullato da un acquisto incompatibile',
  replaced: 'sostituito',
  removed: 'tolto',
};

const SOURCE_LABEL: Record<string, string> = {
  auto_trade: 'motore',
  agent: 'studio agentico',
  manual: 'tu',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

/** Toglie i parametri vuoti: il server rifiuta una stringa vuota dove si aspetta una data. */
function cleanParams(params: StrategyParams): StrategyParams {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => typeof value === 'string' && value.trim() !== '')) as StrategyParams;
}

export function StrategyTagsModal({ portfolioId, instrumentId, instrumentName, onClose }: StrategyTagsModalProps) {
  const catalog = useStrategyCatalog();
  const position = usePositionStrategy(portfolioId, instrumentId);
  const save = useSavePositionStrategy(portfolioId, instrumentId);
  // `null` = bozza non ancora inizializzata dai tag attuali.
  const [draft, setDraft] = useState<Map<StrategyTag, StrategyParams> | null>(null);

  const items = useMemo(() => catalog.data?.items ?? [], [catalog.data]);
  const current = useMemo(
    () => draft ?? new Map((position.data?.active ?? []).map((tag) => [tag.tag, tag.params])),
    [draft, position.data],
  );

  const blockedBy = (item: StrategyCatalogItem): StrategyCatalogItem | null => {
    if (current.has(item.tag) || item.dimension === 'horizon') return null;
    const other = items.find((candidate) => current.has(candidate.tag) && candidate.conflictsWith.includes(item.tag));
    return other ?? null;
  };

  const toggle = (item: StrategyCatalogItem) => {
    const next = new Map(current);
    if (next.has(item.tag)) {
      next.delete(item.tag);
    } else {
      // Un orizzonte nuovo sostituisce quello di prima, e toglie cio' che con
      // il nuovo non puo' stare: e' la scelta piu' recente a valere.
      for (const conflict of item.conflictsWith) next.delete(conflict);
      next.set(item.tag, {});
    }
    setDraft(next);
  };

  const setParam = (tag: StrategyTag, key: StrategyParamKey, value: string) => {
    const next = new Map(current);
    next.set(tag, { ...(next.get(tag) ?? {}), [key]: value });
    setDraft(next);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // L'ordine del catalogo, non quello dei click.
    const tags = items.filter((item) => current.has(item.tag)).map((item) => ({ tag: item.tag, params: cleanParams(current.get(item.tag) ?? {}) }));
    save.mutate(tags, { onSuccess: onClose });
  };

  const loading = catalog.isPending || position.isPending;
  const history = position.data?.history ?? [];

  return (
    <Modal title={`Strategia — ${instrumentName}`} onClose={onClose}>
      {loading ? (
        <Spinner />
      ) : catalog.isError || position.isError ? (
        <Alert variant="error" title="Tag di strategia non disponibili">
          {errorMessage(catalog.error ?? position.error)}
        </Alert>
      ) : (
        <form onSubmit={submit} className="strategy-modal">
          <p className="strategy-modal__hint">
            Perché tieni questa posizione e come va gestita. Li leggono i revisori che decidono cosa vendere.
          </p>
          {save.isError && (
            <Alert variant="error" title="Tag non salvati">
              {errorMessage(save.error)}
            </Alert>
          )}

          {(['horizon', 'thesis', 'modifier'] as const).map((dimension) => (
            <fieldset key={dimension} className="strategy-modal__group">
              <legend>{DIMENSION_TITLES[dimension]}</legend>
              {items
                .filter((item) => item.dimension === dimension)
                .map((item) => {
                  const blocker = blockedBy(item);
                  const selected = current.has(item.tag);
                  return (
                    <div key={item.tag} className="strategy-modal__option">
                      <label title={blocker ? `Incompatibile con «${blocker.label}»` : item.exitGuidance}>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={blocker !== null}
                          onChange={() => toggle(item)}
                        />{' '}
                        <strong>{item.label}</strong>
                        <span className="strategy-modal__description"> — {item.description}</span>
                      </label>
                      {selected && item.params.length > 0 && (
                        <div className="strategy-modal__params">
                          {item.params.map((key) =>
                            key === 'eventTiming' ? (
                              <Select
                                key={key}
                                label={PARAM_LABELS[key]}
                                placeholder="—"
                                options={TIMING_OPTIONS}
                                value={current.get(item.tag)?.eventTiming ?? ''}
                                onChange={(event) => setParam(item.tag, key, event.target.value)}
                              />
                            ) : (
                              <Field
                                key={key}
                                label={PARAM_LABELS[key]}
                                type={key === 'eventDate' || key === 'reviewBy' ? 'date' : 'text'}
                                numeric={key === 'targetPrice' || key === 'stopPrice'}
                                inputMode={key === 'targetPrice' || key === 'stopPrice' ? 'decimal' : undefined}
                                maxLength={key === 'eventLabel' || key === 'note' ? 300 : undefined}
                                value={current.get(item.tag)?.[key] ?? ''}
                                onChange={(event) => setParam(item.tag, key, event.target.value)}
                              />
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </fieldset>
          ))}

          {history.length > 0 && (
            <details className="strategy-modal__history">
              <summary>Storico ({history.length})</summary>
              <ul>
                {history.slice(0, 10).map((entry) => {
                  const label = items.find((item) => item.tag === entry.tag)?.label ?? entry.tag;
                  const details = describeParams(entry.params);
                  return (
                    <li key={entry.id}>
                      {label}
                      {details ? ` (${details})` : ''} — da {SOURCE_LABEL[entry.source] ?? entry.source} il {formatDate(entry.appliedAt)}
                      {entry.closedAt ? `, ${CLOSED_REASON[entry.closedReason ?? ''] ?? entry.closedReason} il ${formatDate(entry.closedAt)}` : ''}
                      {entry.reason ? ` · ${entry.reason}` : ''}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}

          <footer className="strategy-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" loading={save.isPending}>
              Salva
            </Button>
          </footer>
        </form>
      )}
    </Modal>
  );
}
